// frontend/src/pages/shop/ShopCodPayments.jsx
import { useState, useEffect } from 'react';
import { codSettlementAPI } from '../../services/api';
import ShopLayout from '../../components/layout/ShopLayout';

const ShopCodPayments = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  
  // Filter
  const [dateRange, setDateRange] = useState('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Confirm/Reject Modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  // Хүлээгдэж буй төлбөрүүд татах
  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const response = await codSettlementAPI.getShopPendingPayments();
      if (response.data.success) {
        setPendingPayments(response.data.data.payments || []);
      }
    } catch (error) {
      console.error('Pending payments fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Бүх төлбөрүүд татах
  const fetchAllPayments = async () => {
    setLoading(true);
    try {
      const params = { date_range: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      const response = await codSettlementAPI.getShopCodPayments(params);
      if (response.data.success) {
        setAllPayments(response.data.data.payments || []);
        setSummary(response.data.data.summary || null);
      }
    } catch (error) {
      console.error('All payments fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingPayments();
    } else {
      fetchAllPayments();
    }
  }, [activeTab]);

  // Баталгаажуулах
  const handleConfirm = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      const response = await codSettlementAPI.confirmCodPayment(selectedPayment.id, {
        notes: confirmNotes
      });
      if (response.data.success) {
        alert('Төлбөр амжилттай баталгаажлаа');
        setShowConfirmModal(false);
        setSelectedPayment(null);
        setConfirmNotes('');
        fetchPendingPayments();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Баталгаажуулахад алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  // Татгалзах
  const handleReject = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      const response = await codSettlementAPI.rejectCodPayment(selectedPayment.id, {
        rejection_notes: rejectReason
      });
      if (response.data.success) {
        alert('Төлбөр татгалзсан');
        setShowRejectModal(false);
        setSelectedPayment(null);
        setRejectReason('');
        fetchPendingPayments();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Татгалзахад алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmModal = (payment) => {
    setSelectedPayment(payment);
    setConfirmNotes('');
    setShowConfirmModal(true);
  };

  const openRejectModal = (payment) => {
    setSelectedPayment(payment);
    setRejectReason('');
    setShowRejectModal(true);
  };

  return (
    <ShopLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">💵 COD төлбөр</h1>
            <p className="text-sm text-gray-500 mt-1">Хүргэлтийн компаниас ирсэн төлбөрүүд</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1 max-w-sm">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'pending'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>🕐</span>
                <span>Хүлээгдэж буй</span>
                {pendingPayments.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === 'pending' ? 'bg-white/30' : 'bg-amber-500 text-white'
                  }`}>
                    {pendingPayments.length}
                  </span>
                )}
              </span>
            </button>
             <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                activeTab === 'all'
                  ? 'bg-indigo-500 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <span>💰</span>
                <span>COD тооцоо</span>
              </span>
            </button>
          </div>
        </div>

        {/* Filter - Only for "all" tab */}
        {activeTab === 'all' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="today">Өнөөдөр</option>
                  <option value="yesterday">Өчигдөр</option>
                  <option value="week">Сүүлийн 7 хоног</option>
                  <option value="month">Сүүлийн 30 хоног</option>
                  <option value="custom">Сонгох</option>
                </select>
              </div>
              {dateRange === 'custom' && (
                <>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </>
              )}
              <button
                onClick={fetchAllPayments}
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  '🔍'
                )}
                <span>Хайх</span>
              </button>
            </div>
          </div>
        )}

        {/* Summary Cards - Pending Tab */}
        {activeTab === 'pending' && pendingPayments.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-amber-100 text-sm font-medium">Хүлээгдэж буй</p>
                  <p className="text-3xl font-bold mt-2 text-white">{pendingPayments.length}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🕐</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-indigo-100 text-sm font-medium">Нийт дүн</p>
                  <p className="text-2xl font-bold mt-2 text-white">
                    {formatCurrency(pendingPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">💰</span>
                </div>
              </div>
            </div>
          </div>
        )}

       {/* Summary Cards - All Tab */}
        {activeTab === 'all' && summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Карт 1: Нийт авах COD дүн */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-medium">Нийт авах COD</p>
                  <p className="text-xl font-bold mt-1 text-white">{formatCurrency(summary.total_amount || 0)}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">💰</span>
                </div>
              </div>
            </div>
            {/* Карт 2: Хүлээгдэж буй дүн */}
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-amber-100 text-xs font-medium">Хүлээгдэж буй</p>
                  <p className="text-xl font-bold mt-1 text-white">{formatCurrency(summary.pending_amount || 0)}</p>
                  <p className="text-amber-200 text-xs mt-1">{summary.pending || 0} гүйлгээ</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">🕐</span>
                </div>
              </div>
            </div>
            {/* Карт 3: Баталгаажсан дүн */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-emerald-100 text-xs font-medium">Баталгаажсан</p>
                  <p className="text-xl font-bold mt-1 text-white">{formatCurrency(summary.confirmed_amount || 0)}</p>
                  <p className="text-emerald-200 text-xs mt-1">{summary.confirmed || 0} гүйлгээ</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">✅</span>
                </div>
              </div>
            </div>
             {/* Карт 4: Төлөх үлдэгдэл */}
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-rose-100 text-xs font-medium">Авах үлдэгдэл</p>
                  <p className="text-xl font-bold mt-1 text-white">{formatCurrency(summary.balance || 0)}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">💸</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
             <h3 className="font-semibold text-gray-900">
              {activeTab === 'pending' ? '🕐 Хүлээгдэж буй төлбөрүүд' : '💰 COD тооцооны түүх'}
            </h3>
            <span className="text-sm text-gray-500">
              {(activeTab === 'pending' ? pendingPayments : allPayments).length} бичлэг
            </span>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
              {(activeTab === 'pending' ? pendingPayments : allPayments).length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <span className="text-4xl">📭</span>
                  <p className="mt-2">{activeTab === 'pending' ? 'Хүлээгдэж буй төлбөр байхгүй' : 'Төлбөрийн түүх байхгүй'}</p>
                </div>
              ) : (
                (activeTab === 'pending' ? pendingPayments : allPayments).map((payment) => (
                  <div key={payment.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                            payment.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {payment.status === 'confirmed' ? '✅ Баталгаажсан' :
                             payment.status === 'rejected' ? '❌ Татгалзсан' :
                             '🕐 Хүлээгдэж байна'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(payment.payment_date)}
                          </span>
                        </div>
                        <p className="text-lg font-bold text-gray-900 mt-2">{formatCurrency(payment.amount)}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {payment.payment_method === 'bank_transfer' ? '🏦 Банкны шилжүүлэг' : 
                           payment.payment_method === 'cash' ? '💵 Бэлэн' : '📱 QPay'}
                          {payment.payment_reference && ` • ${payment.payment_reference}`}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Бүртгэсэн: {payment.created_by_name || 'Компани'}
                        </p>
                        {payment.notes && (
                          <p className="text-xs text-gray-500 mt-1 bg-gray-50 px-2 py-1 rounded">💬 {payment.notes}</p>
                        )}
                        {payment.rejection_notes && (
                          <p className="text-xs text-rose-600 mt-1 bg-rose-50 px-2 py-1 rounded">❌ {payment.rejection_notes}</p>
                        )}
                      </div>
                      
                      {activeTab === 'pending' && payment.status === 'pending' && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => openConfirmModal(payment)}
                            className="px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors whitespace-nowrap"
                          >
                            ✅ Баталгаажуулах
                          </button>
                          <button
                            onClick={() => openRejectModal(payment)}
                            className="px-3 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 transition-colors whitespace-nowrap"
                          >
                            ❌ Татгалзах
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Confirm Modal */}
        {showConfirmModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-green-50">
                <h2 className="text-lg font-bold text-gray-900">Төлбөр баталгаажуулах</h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-emerald-50 rounded-lg p-4 text-center border border-emerald-200">
                  <p className="text-sm text-emerald-600">Баталгаажуулах дүн</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Тайлбар (заавал биш)</label>
                  <textarea
                    value={confirmNotes}
                    onChange={(e) => setConfirmNotes(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    rows={2}
                    placeholder="Тайлбар бичих..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Болих
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Уншиж байна...' : '✅ Баталгаажуулах'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-rose-50 to-red-50">
                <h2 className="text-lg font-bold text-gray-900">Төлбөр татгалзах</h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-rose-50 rounded-lg p-4 text-center border border-rose-200">
                  <p className="text-sm text-rose-600">Татгалзах дүн</p>
                  <p className="text-2xl font-bold text-rose-700">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Татгалзсан шалтгаан</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                    rows={3}
                    placeholder="Шалтгаан бичнэ үү..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Болих
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Уншиж байна...' : '❌ Татгалзах'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

export default ShopCodPayments;